import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";

import {
  EmptyState,
  Panel,
  SheetLabel,
  Skeleton,
  Status,
  utilityLabel,
} from "@/components/primitives";
import { api, type UtilityName } from "@/lib/api";
import { formatASQ, formatDateTime, shortHash } from "@/lib/format";

/**
 * Activity — plan §39, §37A.
 *
 * A receipt ledger, not a table. Each entry is laid out as a printed receipt
 * line: reference on the left, what it bought in the middle, the amount and its
 * confirmation on the right. That survives a 360px viewport, which a table with
 * six columns does not — and §37A rules out a generic mobile table explicitly.
 */

const FILTERS: { value: string; label: string }[] = [
  { value: "", label: "Semua" },
  { value: "ASETQU_MEMBERSHIP", label: "Membership" },
  { value: "ASETQU_EVOUCHER", label: "E-Voucher" },
  { value: "ASETQU_CATALOG", label: "Catalog" },
];

export function ActivityPage() {
  const { address, isConnected } = useAccount();
  const [filter, setFilter] = useState("");

  const { data, isPending } = useQuery({
    queryKey: ["activity", address, filter],
    queryFn: () => api.activity(address!, filter || undefined),
    enabled: Boolean(address),
  });

  if (!isConnected) {
    return (
      <div className="sheet py-24">
        <EmptyState
          title="Wallet belum terhubung"
          action={
            <Link to="/membership" className="btn btn--ghost">
              Lihat utility
            </Link>
          }
        >
          Riwayat pembayaran terikat pada alamat wallet. Hubungkan wallet untuk
          melihat milik Anda.
        </EmptyState>
      </div>
    );
  }

  const entries = data?.activity ?? [];

  return (
    <div className="sheet py-16">
      <SheetLabel coordinate="LEDGER">Utility activity</SheetLabel>
      <h1 className="display-section mt-5 text-paper-50">Activity</h1>
      <p className="mt-5 max-w-xl text-sm text-paper-300/70">
        Setiap baris di sini adalah pembayaran yang sudah diverifikasi dari
        receipt on-chain. Transaksi yang gagal atau belum terkonfirmasi tidak
        pernah tercatat.
      </p>

      <nav aria-label="Filter" className="mt-10 border-y border-ink-700">
        <ul className="flex flex-wrap">
          {FILTERS.map((entry) => (
            <li key={entry.value || "all"}>
              <button
                type="button"
                onClick={() => setFilter(entry.value)}
                aria-pressed={filter === entry.value}
                className={`micro border-r border-ink-700 px-5 py-4 transition-colors ${
                  filter === entry.value ? "text-hub-700" : "hover:text-paper-50"
                }`}
              >
                {entry.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {isPending ? (
        <div className="mt-8 space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : entries.length === 0 ? (
        <div className="mt-10">
          <EmptyState
            title="Belum ada aktivitas"
            action={
              <Link to="/membership" className="btn btn--primary">
                Mulai dari Membership
              </Link>
            }
          >
            Pembayaran utility akan muncul di sini setelah event-nya
            diverifikasi.
          </EmptyState>
        </div>
      ) : (
        <ol className="mt-8 space-y-3">
          {entries.map((entry, index) => (
            <li key={entry.id}>
              <Panel className="grid gap-4 p-5 sm:grid-cols-12 sm:items-center">
                <div className="sm:col-span-3">
                  <p className="micro">
                    TX / {String(entries.length - index).padStart(4, "0")}
                  </p>
                  <p className="micro mt-1.5">{formatDateTime(entry.createdAt)}</p>
                </div>

                <div className="sm:col-span-5">
                  <p className="micro text-hub-700">{utilityLabel(entry.utilityName)}</p>
                  <p className="mt-1.5 text-sm text-paper-50">{entry.label}</p>
                </div>

                <div className="sm:col-span-2">
                  <p className="datum text-sm text-asq-gold">
                    −{formatASQ(entry.amountRaw)} ASQ
                  </p>
                  <div className="mt-1.5">
                    <Status state="ok">{entry.status}</Status>
                  </div>
                </div>

                <div className="sm:col-span-2 sm:text-right">
                  {entry.explorerUrl ? (
                    <a
                      className="hexline linkline text-hub-300"
                      href={entry.explorerUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {shortHash(entry.transactionHash)}
                    </a>
                  ) : (
                    <span className="hexline text-paper-300/50">
                      {shortHash(entry.transactionHash)}
                    </span>
                  )}
                  <p className="micro mt-1.5">Blok {entry.blockNumber}</p>
                </div>
              </Panel>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Orders                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Orders — plan §24.
 *
 * The post-payment states are moved by hand in staging, so the controls are
 * here. PAID is absent from them on purpose: the server refuses to write it
 * from this endpoint, and offering a button that always fails would be worse
 * than not offering one.
 */
export function OrdersPage() {
  const { address, isConnected } = useAccount();
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ["orders", address],
    queryFn: () => api.orders(address!),
    enabled: Boolean(address),
  });

  const advance = async (id: string, status: string) => {
    await api.setOrderStatus(id, status);
    queryClient.invalidateQueries({ queryKey: ["orders", address] });
  };

  if (!isConnected) {
    return (
      <div className="sheet py-24">
        <EmptyState title="Wallet belum terhubung">
          Order terikat pada alamat wallet yang membayarnya.
        </EmptyState>
      </div>
    );
  }

  const orders = data?.orders ?? [];

  return (
    <div className="sheet py-16">
      <SheetLabel coordinate="ASETQU_CATALOG">Fulfilment</SheetLabel>
      <h1 className="display-section mt-5 text-paper-50">Orders</h1>

      {isPending ? (
        <div className="mt-10 space-y-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      ) : orders.length === 0 ? (
        <div className="mt-10">
          <EmptyState
            title="Belum ada order"
            action={
              <Link to="/catalog" className="btn btn--primary">
                Buka katalog
              </Link>
            }
          >
            Order dibuat saat Anda memulai pembayaran katalog, dan menjadi PAID
            setelah event-nya diverifikasi.
          </EmptyState>
        </div>
      ) : (
        <ul className="mt-10 space-y-4">
          {orders.map((order) => (
            <li key={order.id}>
              <Panel className="grid gap-5 p-6 lg:grid-cols-12 lg:items-center">
                <div className="lg:col-span-4">
                  <p className="micro">{order.orderNumber}</p>
                  <p className="mt-2 text-sm text-paper-50">{order.product?.name}</p>
                  <p className="micro mt-1">{order.product?.provider}</p>
                </div>

                <div className="lg:col-span-2">
                  <p className="datum text-sm text-asq-gold">
                    {formatASQ(order.amountRaw)} ASQ
                  </p>
                </div>

                <div className="lg:col-span-3">
                  <Status state={order.status === "PENDING_PAYMENT" ? "warn" : "ok"}>
                    {order.status}
                  </Status>
                  <p className="micro mt-1.5">{formatDateTime(order.updatedAt)}</p>
                </div>

                <div className="flex flex-wrap gap-2 lg:col-span-3 lg:justify-end">
                  {order.status === "PAID" ? (
                    <button
                      type="button"
                      className="btn btn--ghost !min-h-9 !text-[0.6875rem]"
                      onClick={() => advance(order.id, "PROCESSING")}
                    >
                      Tandai diproses
                    </button>
                  ) : null}
                  {order.status === "PROCESSING" ? (
                    <button
                      type="button"
                      className="btn btn--ghost !min-h-9 !text-[0.6875rem]"
                      onClick={() => advance(order.id, "COMPLETED")}
                    >
                      Tandai selesai
                    </button>
                  ) : null}
                  {order.explorerUrl ? (
                    <a
                      className="hexline linkline self-center text-hub-300"
                      href={order.explorerUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {shortHash(order.transactionHash)}
                    </a>
                  ) : null}
                </div>
              </Panel>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Re-exported so the router can name every page from one module per concern.
export type { UtilityName };
